/**
 * NotificationsSidesheet — Painel lateral de notificações (alertas recentes).
 *
 * Abre ao clicar no sino do header. Mostra os últimos alertas ACTIVE
 * com cores por severidade. Clicar em um alerta navega para /alerts.
 *
 * Requirements: 10.6
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import type { AlertRecord, AlertSeverity } from '@smartsafe/shared';
import Sidesheet from './Sidesheet';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface NotificationsSidesheetProps {
  open: boolean;
  onClose: () => void;
}

interface AlertsResponse {
  data: Array<AlertRecord & { deviceName?: string }>;
  nextCursor: string | null;
  total: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  'CRÍTICO': '#ef4444',
  'AVISO': '#f59e0b',
  'INFORMAÇÃO': '#3b82f6',
};

const SEVERITY_BG: Record<AlertSeverity, string> = {
  'CRÍTICO': 'rgba(239, 68, 68, 0.1)',
  'AVISO': 'rgba(245, 158, 11, 0.1)',
  'INFORMAÇÃO': 'rgba(59, 130, 246, 0.1)',
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const created = new Date(isoDate).getTime();
  const diffMs = now - created;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `há ${diffD}d`;
}

// ─── Fetch ──────────────────────────────────────────────────────────────────────

async function fetchRecentAlerts(): Promise<AlertsResponse> {
  const res = await fetch('/bff/alerts?status=ACTIVE&limit=10');
  if (!res.ok) return { data: [], nextCursor: null, total: 0 };
  return (await res.json()) as AlertsResponse;
}

// ─── Component ──────────────────────────────────────────────────────────────────

export default function NotificationsSidesheet({ open, onClose }: NotificationsSidesheetProps) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<AlertsResponse>({
    queryKey: ['m12-alerts', 'notifications-panel'],
    queryFn: fetchRecentAlerts,
    enabled: open,
    staleTime: 15_000,
  });

  const alerts = (data?.data ?? []).filter((a) => !a.acknowledgedAt);

  function handleAlertClick(alertId: string) {
    onClose();
    void navigate({ to: '/alerts', search: { alertId } });
  }

  function handleViewAll() {
    onClose();
    void navigate({ to: '/alerts' });
  }

  return (
    <Sidesheet open={open} onClose={onClose} title="Notificações">
      <div style={styles.container}>
        {isLoading && (
          <div style={styles.loading} data-testid="notifications-loading">
            Carregando...
          </div>
        )}

        {!isLoading && alerts.length === 0 && (
          <div style={styles.empty} data-testid="notifications-empty">
            Nenhuma notificação pendente.
          </div>
        )}

        {alerts.map((alert) => (
          <button
            key={alert.alertId}
            type="button"
            style={{
              ...styles.alertCard,
              borderLeftColor: SEVERITY_COLORS[alert.severity],
              backgroundColor: SEVERITY_BG[alert.severity],
            }}
            onClick={() => { handleAlertClick(alert.alertId); }}
            data-testid={`notification-item-${alert.alertId}`}
          >
            <div style={styles.alertHeader}>
              <span style={{ ...styles.severityDot, backgroundColor: SEVERITY_COLORS[alert.severity] }} />
              <span style={styles.severityLabel}>{alert.severity}</span>
              <span style={styles.alertTime}>{formatRelativeTime(alert.createdAt)}</span>
            </div>
            <div style={styles.alertMessage}>{alert.message}</div>
            <div style={styles.alertDevice}>{alert.deviceName ?? alert.deviceId}</div>
          </button>
        ))}

        {alerts.length > 0 && (
          <button
            type="button"
            style={styles.viewAllBtn}
            onClick={handleViewAll}
            data-testid="notifications-view-all"
          >
            Ver todos os alertas →
          </button>
        )}
      </div>
    </Sidesheet>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  loading: {
    padding: 24,
    textAlign: 'center',
    color: 'var(--text-3, #667)',
    fontSize: '13px',
  },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: 'var(--text-3, #667)',
    fontSize: '13px',
  },
  alertCard: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '12px 14px',
    borderRadius: 8,
    border: 'none',
    borderLeft: '4px solid',
    cursor: 'pointer',
    transition: 'opacity 150ms',
    background: 'transparent',
  },
  alertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  severityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  severityLabel: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-2, #aab)',
  },
  alertTime: {
    marginLeft: 'auto',
    fontSize: '11px',
    color: 'var(--text-3, #667)',
  },
  alertMessage: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-1, #fff)',
    marginBottom: 2,
    lineHeight: 1.4,
  },
  alertDevice: {
    fontSize: '11px',
    color: 'var(--text-3, #667)',
  },
  viewAllBtn: {
    display: 'block',
    width: '100%',
    padding: '12px',
    marginTop: 8,
    background: 'transparent',
    border: '1px solid var(--border, #2a3a4a)',
    borderRadius: 6,
    color: 'var(--primary, #3b82f6)',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'center',
  },
};
